import styled, { css } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import { CarouselBase, CarouselButton, CarouselPaginationBullets, CarouselSlide } from '../Carousel';

const commonMargins = css`
  margin-left: 0;
  margin-right: 0;
  .container {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const TabComponentContainer = styled.div`
  ${commonMargins}
  display: flex;
  justify-content: center;

  div {
    span {
      font-weight: 500;
    }
  }

  .tab-wrapper {
    padding-top: 20px;
    padding-bottom: 0px;
    border-radius: 0;
    box-shadow: none;

    ul.m-standard-tab-list {
      margin-bottom: 24px;

      // Overriding the spacing on the TabsEditable Component
      li > a > div {
        margin-bottom: 12px;
      }

      li:not(:last-child) {
        margin-right: 32px !important;
      }

      li:not(.active) > a > div > span {
        color: ${baseVariables.color['neutral30']} !important;
      }
    }
  }
`;

export const Eyebrow = styled.div`
  ${commonMargins}

  font-family: Swiss-721;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 16px;
`;

export const Title = styled.div`
  ${commonMargins}

  font-family: Swiss-721;
  font-size: 29px;
  font-weight: 700;
  margin-bottom: 16px;
`;

export const Subtitle = styled.div`
  ${commonMargins}

  font-family: Swiss-721;
  font-size: 16px;
  font-weight: 500;
  text-overflow: ellipsis;
  margin-bottom: 20px;
  -webkit-line-clamp: 4;
`;

export const ThemeClusterWrapper = styled.div`
  width: 100%;
  padding-top: 32px;
  padding-bottom: 32px;

  & > *:nth-last-child(2) {
    margin-top: unset;
    margin-bottom: unset;
  }
`;

export const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 32px;
`;

export const StyledCarouselPaginationBullets = styled(CarouselPaginationBullets)`
  top: 1px !important;
  left: 1px !important;
  min-height: 0.75rem;
  & .swiper-pagination-bullet {
    background: ${baseVariables.color.neutral10};
    margin-top: 3px;
  }
`;

// This should follow settings defined in Swiper settings for Theme Cluster
export const ThemeClusterButtonWrapper = styled.div<{ numberOfSlides: number }>`
  align-self: center;
  flex-direction: row;
  margin-top: 1rem !important;
  width: auto;
  /* Prevent layout shifts during carousel transitions */
  position: relative;
  min-height: 0;
  flex-shrink: 0;
  /* Ensure buttons don't move when carousel shifts - prevent Swiper from animating this container */
  transform: translateZ(0);
  will-change: auto;
  /* Prevent any transitions that might cause buttons to animate */
  transition: none !important;

  ${({ numberOfSlides }) => {
    if (numberOfSlides > 1) {
      return 'display: flex;';
    }

    return `display: none;`;
  }}

  @media (min-width: 768px) {
    ${({ numberOfSlides }) => {
      if (numberOfSlides > 2) {
        return 'display: flex;';
      }

      return `display: none;`;
    }}
  }

  @media (min-width: 1200px) {
    ${({ numberOfSlides }) => {
      if (numberOfSlides > 3) {
        return 'display: flex;';
      }

      return `display: none;`;
    }}
  }
`;

export const StyledCarouselButton = styled(CarouselButton)`
  background-color: transparent;
  height: 20px;
  position: relative;
  width: 20px;

  &:focus {
    outline: 1px dotted;
    outline: 5px auto -webkit-focus-ring-color;
  }

  & > * {
    position: relative;
    height: 20px;
    width: 20px;
  }

  .icon-arrow-left,
  .icon-arrow-right {
    position: absolute;
    transform: translate(-50%, -50%);

    &:before {
      margin-top: 50%;
      font-size: 25px;
      color: ${baseVariables.color.neutral10};
      margin-left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

export const StyledNonDesktopCarouselButton = styled(StyledCarouselButton)`
  @media${baseVariables.mediaQuery.xl} {
    display: none;
  }
`;

export const StyledContainerStartElement = styled.div<{ $isInitialized?: boolean }>`
  display: none;

  @media${baseVariables.mediaQuery.xl} {
    display: ${({ $isInitialized }) => ($isInitialized ? 'flex' : 'none')};
    justify-content: space-between;
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    width: 100%;
    z-index: 3;

    ${StyledCarouselButton} {
      background: white;
      border-radius: 100%;
      height: 40px;
      width: 40px;

      & .icon-arrow-right:before,
      .icon-arrow-left:before {
        color: ${baseVariables.color.neutral00};
      }
    }
  }
`;

export const StyledDesktopCarouselButton = styled(StyledCarouselButton)`
  ${({ direction }) => {
    if (direction === 'left') {
      return 'left: -20px;';
    }

    return 'right: -20px;';
  }}
`;

type ThemeCarouselBaseStyleProps = {
  $isInitialized?: boolean;
  $totalNumberOfSlides?: number;
  $numberOfSlidesMedium?: number;
  $numberOfSlidesLarge?: number;
  $disablePreviousButton?: boolean;
  $disableNextButton?: boolean;
};

export const ThemeCarouselBase = styled(CarouselBase)`
  ${({ $disablePreviousButton }: ThemeCarouselBaseStyleProps) => {
    if ($disablePreviousButton) {
      return `
        .carousel-left-arrow span::before{
          color: var(--t-brand-alt-color);
          opacity: 50%;
        }
        
         @media${baseVariables.mediaQuery.xl}{ 
          .carousel-left-arrow {
            visibility: hidden;
          }
         }

      `;
    }

    return '';
  }}

  ${({ $disableNextButton }: ThemeCarouselBaseStyleProps) => {
    if ($disableNextButton) {
      return `
        .carousel-right-arrow span::before{
          color: var(--t-brand-alt-color);
          opacity: 50%;
        }
         @media${baseVariables.mediaQuery.xl}{ 
          .carousel-right-arrow {
            display: none;
          }
         }
      `;
    }

    return '';
  }}

  ${({ $totalNumberOfSlides, $numberOfSlidesMedium }: ThemeCarouselBaseStyleProps) => {
    const hideCarouselArrow =
      typeof $numberOfSlidesMedium === 'number' &&
      $totalNumberOfSlides &&
      $totalNumberOfSlides <= $numberOfSlidesMedium;

    return (
      hideCarouselArrow &&
      `@media(min-width: 768px) {
        .carousel-right-arrow {
          display: none;
        }
      }`
    );
  }}

  ${({ $totalNumberOfSlides, $numberOfSlidesLarge }: ThemeCarouselBaseStyleProps) => {
    const hideCarouselArrow =
      typeof $numberOfSlidesLarge === 'number' && $totalNumberOfSlides && $totalNumberOfSlides <= $numberOfSlidesLarge;

    return (
      hideCarouselArrow &&
      `@media(min-width: 1200px) {
        .carousel-right-arrow {
          display: none;
        }
      }`
    );
  }}

  ${({ $isInitialized }: ThemeCarouselBaseStyleProps) => {
    if ($isInitialized) {
      return '';
    }

    // The following styles are added to accomodate first render (when Swiper js is not yet set up)
    return css`
      .swiper-wrapper {
        display: flex;
        flex-direction: row;
        gap: 0;

        @media (min-width: 768px) {
          gap: 24px;
        }

        .swiper-slide {
          display: none;
          flex: 0 0 100%;
          width: 100%;
          min-width: 0;

          &:first-child {
            display: flex;
          }

          @media (min-width: 768px) {
            ${({ $numberOfSlidesMedium }: ThemeCarouselBaseStyleProps) => {
              const slidesPerView =
                typeof $numberOfSlidesMedium === 'number' && $numberOfSlidesMedium > 1 ? $numberOfSlidesMedium : 1;

              if (slidesPerView === 1) {
                return '';
              }

              return `
                flex: 0 0 calc((100% - 24px * (${slidesPerView} - 1)) / ${slidesPerView});
                width: calc((100% - 24px * (${slidesPerView} - 1)) / ${slidesPerView});
              `;
            }}
          }

          @media (min-width: 1200px) {
            ${({ $numberOfSlidesLarge }: ThemeCarouselBaseStyleProps) => {
              const slidesPerView =
                typeof $numberOfSlidesLarge === 'number' && $numberOfSlidesLarge > 1 ? $numberOfSlidesLarge : 1;

              if (slidesPerView === 1) {
                return '';
              }

              return `
                flex: 0 0 calc((100% - 24px * (${slidesPerView} - 1)) / ${slidesPerView});
                width: calc((100% - 24px * (${slidesPerView} - 1)) / ${slidesPerView});
              `;
            }}
          }

          ${({ $totalNumberOfSlides, $numberOfSlidesMedium }: ThemeCarouselBaseStyleProps) => {
            if (!$totalNumberOfSlides) {
              return '';
            }
            const shouldShowMediumSlideCount =
              typeof $numberOfSlidesMedium === 'number' ? $numberOfSlidesMedium > 1 : true;
            return (
              $totalNumberOfSlides > 1 &&
              shouldShowMediumSlideCount &&
              `
              @media(min-width: 768px) {
                &:nth-child(-n + ${$numberOfSlidesMedium || 2}) {
                  display: flex;
                }
              }
            `
            );
          }}

          ${({ $totalNumberOfSlides, $numberOfSlidesLarge }: ThemeCarouselBaseStyleProps) => {
            if (!$totalNumberOfSlides) {
              return '';
            }
            const shouldShowLargeSlideCount =
              typeof $numberOfSlidesLarge === 'number' ? $numberOfSlidesLarge > 1 : true;
            return (
              $totalNumberOfSlides > 2 &&
              shouldShowLargeSlideCount &&
              `
              @media(min-width: 1200px) {
                &:nth-child(-n + ${$numberOfSlidesLarge || 3}) {
                  display: flex;
                }
              }
            `
            );
          }}
        }
      }
    `;
  }}
`;

export const CreateCardToPublishWrapper = styled.div`
  margin: 0 auto;
  padding: 0;
  & > *:not(:first-child) {
    margin-top: ${toRem(24)};
  }
`;

export const TabContentWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 -20px;
  padding: 0 20px;
`;

interface TabContentContainerProps {
  $isVisible: boolean;
}

export const TabContentContainer = styled(CreateCardToPublishWrapper)<TabContentContainerProps>`
  display: ${props => (props.$isVisible ? 'block' : 'none')};
  width: 100%;
`;

export const aspectRatios = {
  tall: '2 / 3',
  wide: '16 / 9',
  square: '1 / 1',
};

export const ThemeClusterCarouselSlide = styled(CarouselSlide)`
  & > * {
    z-index: 1;
    height: 100%;

    .card-layered-wrapper {
      height: inherit;
    }

    .card-layered {
      height: 100%;
      height: -moz-available;
      height: -webkit-fill-available;
      height: stretch;
    }
  }

  border-radius: 15px;

  &:has(.card-layered-wrapper.wide),
  &:has(.card-layered-wrapper.wide) img {
    aspect-ratio: ${aspectRatios.wide};
  }
  &:has(.card-layered-wrapper.tall),
  &:has(.card-layered-wrapper.tall) img {
    aspect-ratio: ${aspectRatios.tall};
  }
  &:has(.card-layered-wrapper.square),
  &:has(.card-layered-wrapper.square) img {
    aspect-ratio: ${aspectRatios.square};
  }
`;
